import React from "react";
import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid";
import {
  Start,
  Check,
  DocumentScannerOutlined,
  Clear,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import useInboundRequestService from "../services/inboundrequestservice";
import { useNotificationContext } from "../../context/notificationcontext";
import { useAuthContext } from "../../context/authcontext";

export interface InboundRow {
  inboundRequestId: number;
  statusNumber: number;
}

type ActionConfig = {
  label: string;
  icon: React.ReactElement;
  onClick: (row: InboundRow) => Promise<any> | void;
};

export function useInboundActions(
  refreshGrid?: () => void,
  onDownload?: (row: InboundRow) => void
) {
  const navigate = useNavigate();
  const { setInfo, setError } = useNotificationContext();
  const { userData } = useAuthContext();
  const {
    acceptInboundRequest,
    declineInboundRequest: rejectInboundRequest,
    startCounting,
  } = useInboundRequestService();

  // Map of status → config for the "next step" action
  const nextStepConfig: Record<number, ActionConfig> = {
    2: {
      // Pending
      label: t("Accept"),
      icon: <Check />,
      onClick: (row) =>
        acceptInboundRequest(row.inboundRequestId)
          .then(() => {
            setInfo(t("Request accepted successfully"));
            refreshGrid?.();
          })
          .catch((err) => setError(err)),
    },
    3: {
      // Accepted
      label: t("Generate GRN"),
      icon: <DocumentScannerOutlined />,
      onClick: (row) =>
        navigate("/goodsrecievingdetails", { state: { requestData: row } }),
    },
    6: {
      // Goods Received
      label: t("Move to Counting"),
      icon: <Start />,
      onClick: (row) =>
        startCounting(row.inboundRequestId)
          .then(() => {
            setInfo(t("Moved to counting successfully"));
            refreshGrid?.();
            navigate(`/countingpanel/${row.inboundRequestId}`);
          })
          .catch((err) => setError(err)),
    },
    7: {
      // Counting
      label: t("Count"),
      icon: <Check />,
      onClick: (row) => navigate(`/countingpanel/${row.inboundRequestId}`),
    },
    8: {
      // QC
      label: t("Check"),
      icon: <Check />,
      onClick: (row) => navigate(`/qcpanel/${row.inboundRequestId}`),
    },
    // status - 9 // Put Away
    9: {
      label: t("Process"),
      icon: <Check />,
      onClick: (row) => navigate(`/putawaypanel/${row.inboundRequestId}`),
    },
  };

  /**
   * Returns an array of GridActionsCellItem for the given row params
   */
  return function buildActions(
    params: GridRowParams<InboundRow>
  ): React.ReactNode[] {
    const actions: React.ReactNode[] = [];
    const row = params.row;

    // Special case: Pending should show Accept + Reject
    if (row.statusNumber === 2 && userData?.userType === "S") {
      actions.push(
        <GridActionsCellItem
          key="accept"
          icon={<Check />}
          label={t("Accept")}
          showInMenu
          onClick={() => nextStepConfig[2].onClick(row)}
        />,
        <GridActionsCellItem
          key="reject"
          icon={<Clear />}
          label={t("Reject")}
          showInMenu
          onClick={() =>
            rejectInboundRequest(row.inboundRequestId)
              .then(() => {
                setInfo(t("Request rejected successfully"));
                refreshGrid?.();
              })
              .catch((err) => setError(err))
          }
        />
      );
      return actions;
    }

    // Default "next step"
    const cfg = nextStepConfig[row.statusNumber];
    if (cfg && userData?.userType === "S") {
      actions.push(
        <GridActionsCellItem
          key="next-step"
          icon={cfg.icon}
          label={cfg.label}
          showInMenu
          onClick={() => cfg.onClick(row)}
        />
      );
    }

    // Download action once goods received (>=6)
    if (row.statusNumber >= 6 && onDownload) {
      actions.push(
        <GridActionsCellItem
          key="download"
          icon={<DocumentScannerOutlined />}
          label={t("Download Document")}
          showInMenu
          onClick={() => onDownload(row)}
        />
      );
    }

    return actions;
  };
}
